import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { CSSProperties } from 'react'

import { AgentVerificationApplicationRow, AgentVerificationMatchType } from '@percent/admin-dashboard/api/types'
import { getFormattedDate } from '@percent/admin-dashboard/common/library/utility/date'
import {
  Alert,
  ButtonText,
  CardDashboard,
  type TableColumn,
  Heading,
  IllustratedMessage,
  Loader,
  Table,
  TableBody,
  TableCell,
  TableProvider,
  TableRow,
  semanticColors
} from '@percent/lemonade'
import { MatchTypeBadge, RejectionBadge, StatusBadge } from './agentVerificationStatusBadges'

const StyledHeading = styled(Heading)`
  margin-bottom: 1rem;
`

type MatchTypedApplicationRow = AgentVerificationApplicationRow & { matchType?: AgentVerificationMatchType }

export type AgentVerificationApplicationsTableProps = {
  applications: MatchTypedApplicationRow[] | null
  isLoading: boolean
  heading: string
  totalCount?: number
  getInfoText: (count: number) => string
  emptyText: string
  testId: string
  emptyTestId: string
  showMatchType?: boolean
  onViewDocument?: (application: MatchTypedApplicationRow) => void
}

export function AgentVerificationApplicationsTable({
  applications,
  isLoading,
  heading,
  totalCount,
  getInfoText,
  emptyText,
  testId,
  emptyTestId,
  showMatchType,
  onViewDocument
}: AgentVerificationApplicationsTableProps) {
  const { t } = useTranslation()

  // Full-card loader while fetching; the table below only renders once data is present.
  if (isLoading) return <Loader />

  if (!applications?.length) {
    return (
      <CardDashboard data-testid={emptyTestId}>
        <IllustratedMessage illustration="no-results" description={emptyText} />
      </CardDashboard>
    )
  }

  const columns: TableColumn[] = [
    { id: t('table.organisationName') },
    ...(showMatchType ? [{ id: t('table.matchType') }] : []),
    { id: t('table.agentEmail') },
    { id: t('table.partner') },
    { id: t('table.rejectionReason') },
    { id: t('table.status') },
    { id: t('table.date') },
    { id: t('table.view') }
  ]

  const tableCellStyle: CSSProperties = { color: `${semanticColors.contentSecondary}`, fontWeight: 400 }

  const tableCell = (content: React.JSX.Element | string | null) => (
    <TableCell style={tableCellStyle}>{content}</TableCell>
  )

  return (
    <CardDashboard data-testid={testId}>
      <StyledHeading level="h2">{heading}</StyledHeading>
      <Alert variant="info">{getInfoText(totalCount ?? applications.length)}</Alert>
      <TableProvider
        columns={columns}
        illustration="no-results"
        emptyTableDescription={emptyText}
        // Loading is handled by the early return above; the table only renders with data present.
        isLoading={false}
        totalResults={null}
        emptyTableTitle={emptyText}
      >
        <Table>
          <TableBody>
            {applications.map(application => (
              <TableRow key={application.id}>
                {tableCell(application.organisationName)}
                {showMatchType &&
                  tableCell(application.matchType ? <MatchTypeBadge matchType={application.matchType} /> : null)}
                {tableCell(application.email)}
                {tableCell(application.partnerName)}
                {tableCell(<RejectionBadge rejectionReasonCode={application.rejectionReasonCode} />)}
                {tableCell(<StatusBadge status={application.status} />)}
                {tableCell(getFormattedDate(application.createdAt))}
                {tableCell(
                  onViewDocument ? (
                    <ButtonText onClick={() => onViewDocument(application)}>
                      {t('typography.documentComparison.viewDocument')}
                    </ButtonText>
                  ) : (
                    <a href={`/agent-verification/${application.id}`} target="_blank" rel="noreferrer">
                      <ButtonText icon="expand" />
                    </a>
                  )
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableProvider>
    </CardDashboard>
  )
}
